import {
  createContainer,
  InjectionMode,
  asFunction,
  asClass,
  asValue,
  Constructor,
  BuildResolverOptions,
  Resolver,
  AwilixContainer
} from "awilix/browser";

import IModuleContainer from "./IModuleContainer";
import Application from "../Application";
import Store from "../model/runtime/state/Store";
import Dispatcher from "../model/runtime/state/Dispatcher";
import LocaleProvider from "../service/locale/LocaleProvider";
import { UserPreferences } from "../common/Constants";
import { InMemoryCacheManager } from "../model/runtime/cache/InMemoryCacheManager";
import CachingHttpService from "../service/CachingHttpService";
import HttpService from "../service/HttpService";
import TranslationService from "../service/locale/TranslationService";
import HomeArea from "../areas/HomeArea";
import StorageService from "../service/StorageService";
import CryptographyService from "../service/cryptography/CryptographyService";
import CryptographyProvider from "../model/security/CryptographyProvider";
import RandomValueProvider from "../model/security/RandomValueProvider";
import CookieStorageService from "../service/CookieStorageService";
import InteractorProxy from "../interactor/InteractorProxy";
import ShowHomeInteractor from "../interactor/main/ShowHomeInteractor";
import PaymentArea from "../areas/PaymentArea";
import ShowPaymentConfirmationInteractor from "../interactor/payment/ShowPaymentConfirmationInteractor";
import ValidateTokenInteractor from "../interactor/payment/ValidateTokenInteractor";
import PaymentService from "../service/payment/PaymentService";
import ShowDevicesInteractor from "../interactor/device/ShowDevicesInteractor";
import DeviceArea from "../areas/DeviceArea";
import GetDevicesInteractor from "../interactor/device/GetDevicesInteractor";
import DeviceService from "../service/device/DeviceService";
import RemoveDeviceInteractor from "../interactor/device/RemoveDeviceInteractor";
import ShowSuccessMessageInteractor from "../interactor/notifications/ShowSuccessMessageInteractor";
import NotificationService from "../service/notification/NotificationService";
import ShowErrorMessageInteractor from "../interactor/notifications/ShowErrorMessageInteractor";
import ShowPaymentInstructionInteractor from "../interactor/payment/ShowPaymentInstructionInteractor";
import ShowLoginInteractor from "../interactor/authentication/ShowLoginInteractor";
import AuthenticationArea from "../areas/AuthenticationArea";
import LogoutInteractor from "../interactor/authentication/LogoutInteractor";
import CredentialService from "../service/credential/CredentialService";
import CheckLoginStatusInteractor from "../interactor/authentication/CheckLoginStatusInteractor";
import SaveLanguageToStorageInteractor from "../interactor/credential/SaveLanguageToStorageInteractor";
import HandleApplicationErrorInteractor from "../interactor/main/HandleApplicationErrorInteractor";
import ShowPasswordResetInteractor from "../interactor/authentication/ShowPasswordResetInteractor";
import RequestPasswordResetInteractor from "../interactor/credential/RequestPasswordResetInteractor";
import ShowPasswordResetConfirmationInteractor from "../interactor/authentication/ShowPasswordResetConfirmationInteractor";
import ConfirmPasswordResetInteractor from "../interactor/credential/ConfirmPasswordResetInteractor";

const ModuleContainer = (
  application: Application,
  container?: any
): IModuleContainer => {
  const _application = application;
  const _container =
    container ||
    createContainer({
      injectionMode: InjectionMode.PROXY
    });

  function withCache(moduleName: string, timeToLiveMs?: number) {
    return (c) => ({
      httpService: CachingHttpService({
        http: _container.resolve("http"),
        cacheManager: _container.resolve("cacheManager"),
        options: {
          name: moduleName,
          timeToLive: timeToLiveMs !== undefined ? timeToLiveMs : 0
        }
      })
    });
  }

  function asInteractor<T = {}>(
    Type: Constructor<T>,
    opts?: BuildResolverOptions<T>
  ): Resolver<T> {
    return {
      resolve: (container: AwilixContainer) => {
        var interactor = container.build(asClass(Type, opts));
        return new InteractorProxy({
          interactor,
          handleApplicationError:
            container.resolve<HandleApplicationErrorInteractor>(
              "handleApplicationError"
            ),
          application: _application
        }) as any as T;
      }
    };
  }

  const Config = import.meta.env;

  _container.register({
    // config: asFunction(Configuration).singleton(),
    // configurationProvider: asFunction(
    //   EnvironmentConfigurationProvider
    // ).singleton(),
    application: asValue(_application),
    container: asValue(_container),
    baseUrl: asValue(Config.REACT_APP_API_URL),
    options: asValue(null),
    cacheManager: asFunction(InMemoryCacheManager).singleton(),
    language: asValue(UserPreferences.DEFAULT_LANGUAGE),
    localeProvider: asFunction(LocaleProvider).singleton(),
    dispatcher: asFunction(Dispatcher).singleton(),
    handleApplicationError: asClass(HandleApplicationErrorInteractor),
    store: asFunction(Store)
      .singleton()
      .inject((c) => ({
        dispatcher: _container.resolve("dispatcher"),
        initialState: {},
        defaultTopic: "app"
      })),
    defaultSettings: asValue(Config)
  });

  //services
  _container.register({
    storageService: asFunction(StorageService),
    cookieStorageService: asFunction(CookieStorageService),
    httpService: asFunction(CachingHttpService),
    http: asFunction(HttpService),
    translationService: asFunction(TranslationService),
    cryptographyProvider: asFunction(CryptographyProvider),
    randomValueProvider: asFunction(RandomValueProvider),
    cryptographyService: asFunction(CryptographyService),
    paymentService: asFunction(PaymentService).inject(withCache("payment")),
    deviceService: asFunction(DeviceService).inject(withCache("devices")),
    notificationService: asFunction(NotificationService),
    credentialService: asFunction(CredentialService)
  });

  //areas
  _container.register({
    homeArea: asClass(HomeArea).singleton(),
    paymentArea: asClass(PaymentArea).singleton(),
    deviceArea: asClass(DeviceArea).singleton(),
    authenticationArea: asClass(AuthenticationArea).singleton()
  });

  //interactors
  _container.register({
    showHome: asInteractor(ShowHomeInteractor),
    showPaymentConfirmation: asInteractor(ShowPaymentConfirmationInteractor),
    validateToken: asInteractor(ValidateTokenInteractor),
    showDevices: asInteractor(ShowDevicesInteractor),
    getDevices: asInteractor(GetDevicesInteractor),
    removeDevice: asInteractor(RemoveDeviceInteractor),
    showSuccessMessage: asInteractor(ShowSuccessMessageInteractor),
    showPaymentInstruction: asInteractor(ShowPaymentInstructionInteractor),
    showErrorMessage: asInteractor(ShowErrorMessageInteractor),
    showLogin: asInteractor(ShowLoginInteractor),
    logout: asInteractor(LogoutInteractor),
    checkLoginStatus: asInteractor(CheckLoginStatusInteractor),
    saveLanguageToStorage: asInteractor(SaveLanguageToStorageInteractor),
    showPasswordReset: asInteractor(ShowPasswordResetInteractor),
    requestPasswordReset: asInteractor(RequestPasswordResetInteractor),
    showPasswordResetConfirmation: asInteractor(
      ShowPasswordResetConfirmationInteractor
    ),
    confirmPasswordReset: asInteractor(ConfirmPasswordResetInteractor)
  });

  return {
    asInteractor,
    asFunction,
    register: _container.register,
    resolve<T>(name: string) {
      return _container.resolve(name) as T;
    },
    registerValue(name: string, dependency: any) {
      _container.register(name, asValue(dependency));
    },
    registerClass(name: string, dependency: any) {
      _container.register(name, asClass(dependency));
    },
    registerInteractor(name: string, dependency: any) {
      _container.register(name, asInteractor(dependency));
    },
    registerFunction(name: string, dependency: any, cacheName?: string) {
      _container.register(
        name,
        cacheName
          ? asFunction(dependency).inject(withCache(cacheName))
          : asFunction(dependency)
      );
    },
    createScope() {
      return ModuleContainer(_application, _container.createScope());
    }
  } as IModuleContainer;
};

export default ModuleContainer;
